import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { API } from '@/utils/constants';
import { formatDate } from '@/utils/date';
import { DateRangePicker, getDateBoundaries } from '@/components/common/DateRangePicker';
import { printJobSticker } from '@/utils/sticker';
import { EmailStatusIcons } from '@/components/email-status/EmailStatusIcons';

/**
 * All Jobs Page Component
 * Search and filter all jobs in the system
 */
const AllJobsPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);           // Server-side filtered jobs (already paginated)
  const [totalJobs, setTotalJobs] = useState(0);  // Total in DB matching current filters
  // Feb 2026 — email status summaries keyed by job.id (Brevo delivery/open state)
  const [emailStatusByJob, setEmailStatusByJob] = useState({});
  const [totals, setTotals] = useState({ total_inc_gst: 0, total_ex_gst: 0, total_gst: 0, total_paid: 0 });
  const [showAll, setShowAll] = useState(false);  // Bypass the 200-row cap
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [closedFilter, setClosedFilter] = useState('active'); // 'active', 'closed', 'all'
  const [dateRange, setDateRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  
  // Server-side sort state
  const [sortBy, setSortBy] = useState('job_number');
  const [sortDir, setSortDir] = useState('desc');
  
  // Click a column header to cycle: asc → desc → (re-asc). Always set the column as active.
  const onSortClick = (field) => {
    if (sortBy === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(field === 'job_number' || field === 'date_in' || field === 'date_due' || field === 'total_amount' || field === 'balance_owing' ? 'desc' : 'asc');
    }
  };
  const sortArrow = (field) => sortBy !== field ? '⇅' : (sortDir === 'asc' ? '▲' : '▼');
  
  // SMS Modal State
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsJob, setSmsJob] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [sendingSms, setSendingSms] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Refetch whenever filters change. Search is debounced 250ms.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchJobs();
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [searchTerm, statusFilter, closedFilter, dateRange, customStart, customEnd, showAll, sortBy, sortDir]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateBoundaries(dateRange, customStart, customEnd);
      const params = { limit: showAll ? 0 : 200, sort_by: sortBy, sort_dir: sortDir };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (closedFilter) params.closed_filter = closedFilter;
      if (start) params.start_date = start;
      if (end)   params.end_date = end;
      const response = await axios.get(`${API}/jobs/list-paginated`, { params });
      setJobs(response.data.jobs || []);
      setTotalJobs(response.data.total || 0);
      setTotals(response.data.totals || { total_inc_gst: 0, total_ex_gst: 0, total_gst: 0, total_paid: 0 });
      // Feb 2026 — pull Brevo email statuses in parallel (non-blocking)
      axios.get(`${API}/jobs/email-status-batch?days=180`)
        .then(r => setEmailStatusByJob(r.data.jobs || {}))
        .catch(() => {/* icons just don't render on failure */});
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setSmsTemplates(response.data.sms_templates || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Open SMS Modal
  const openSmsModal = (job) => {
    setSmsJob(job);
    setSmsMessage('');
    setSmsModalOpen(true);
  };

  // Apply SMS Template
  const applySmsTemplate = (template) => {
    if (!smsJob || !settings) return;
    
    let message = template.message;
    message = message.replace('{customer_name}', smsJob.customer_name || '');
    message = message.replace('{job_number}', smsJob.job_number || '');
    message = message.replace('{status}', smsJob.status || '');
    message = message.replace('{balance_owing}', parseFloat(smsJob.balance_owing || 0).toFixed(2));
    message = message.replace('{business_name}', settings.business_info?.business_name || '');
    message = message.replace('{business_phone}', settings.business_info?.phone || '');
    
    setSmsMessage(message);
  };

  // Send SMS
  const sendSms = async () => {
    if (!smsJob || !smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingSms(true);
    try {
      await axios.post(`${API}/sms/send`, {
        job_id: smsJob.id,
        message: smsMessage
      });
      toast.success(`SMS sent to ${smsJob.customer_name}`);
      setSmsModalOpen(false);
      setSmsJob(null);
      setSmsMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // Print Sticker — delegates to the shared helper so the template stays in sync
  // across Dashboard, All Jobs and Job Detail.
  const printSticker = (job) => printJobSticker(job, settings);

  if (loading) {
    return <div className="loading">Loading jobs...</div>;
  }

  return (
    <div className="all-jobs-container" data-testid="all-jobs-page">
      <Card>
        <CardHeader>
          <CardTitle>All Jobs</CardTitle>
          <CardDescription>Search and filter all jobs</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          <div className="search-filter-container" style={{ marginBottom: '1.5rem' }}>
            <div className="search-container" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <Input
                type="text"
                placeholder="Search by Job #, Customer Name, Mobile, or Email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1 }}
                data-testid="job-search-input"
              />
              {searchTerm && (
                <Button
                  variant="outline"
                  onClick={() => setSearchTerm('')}
                  data-testid="clear-search-btn"
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div>
                <Label>Status Filter</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger style={{ width: '200px' }} data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {settings?.statuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Closed Filter</Label>
                <Select value={closedFilter} onValueChange={setClosedFilter}>
                  <SelectTrigger style={{ width: '200px' }} data-testid="closed-filter">
                    <SelectValue placeholder="Active Jobs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active Jobs</SelectItem>
                    <SelectItem value="closed">Closed Jobs</SelectItem>
                    <SelectItem value="all">All Jobs</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div style={{ marginLeft: 'auto', paddingTop: '1.5rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }} data-testid="jobs-result-count">
                <span style={{ fontSize: '0.875rem', color: '#666' }}>
                  {totalJobs > jobs.length
                    ? <>Showing <strong>{jobs.length}</strong> of <strong>{totalJobs.toLocaleString()}</strong> jobs — <em>type to search</em></>
                    : <>Showing <strong>{jobs.length}</strong> {jobs.length === 1 ? 'job' : 'jobs'}</>
                  }
                </span>
                {totalJobs > 200 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAll(s => !s)}
                    data-testid="jobs-show-all-toggle"
                  >
                    {showAll ? 'Show top 200 only (faster)' : `Show all ${totalJobs.toLocaleString()} (may be slow)`}
                  </Button>
                )}
              </div>
            </div>

            {/* Date Range Filter (Date In) */}
            <div style={{ marginTop: '1rem' }} data-testid="jobs-date-range">
              <DateRangePicker
                dateRange={dateRange}
                setDateRange={setDateRange}
                startDate={customStart}
                setStartDate={setCustomStart}
                endDate={customEnd}
                setEndDate={setCustomEnd}
                label="Date In Range"
              />
            </div>
          </div>

          {/* Jobs Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead onClick={() => onSortClick('job_number')}     style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="sort-job_number">     Job # <span style={{ color: sortBy === 'job_number' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('job_number')}</span></TableHead>
                <TableHead onClick={() => onSortClick('customer_name')}  style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="sort-customer_name">  Customer <span style={{ color: sortBy === 'customer_name' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('customer_name')}</span></TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead onClick={() => onSortClick('status')}         style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="sort-status">         Status <span style={{ color: sortBy === 'status' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('status')}</span></TableHead>
                <TableHead>Type</TableHead>
                <TableHead onClick={() => onSortClick('date_in')}        style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="sort-date_in">        Date In <span style={{ color: sortBy === 'date_in' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('date_in')}</span></TableHead>
                <TableHead onClick={() => onSortClick('total_amount')}   style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="sort-total_amount">   Total <span style={{ color: sortBy === 'total_amount' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('total_amount')}</span></TableHead>
                <TableHead>Closed</TableHead>
                <TableHead style={{ minWidth: '180px' }}>
                  <div style={{ display: 'flex', gap: '8px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#64748b', whiteSpace: 'nowrap' }}>
                    <span title="Send SMS via Twilio">SMS</span>
                    <span title="Print sticker">Sticker</span>
                    <span title="Email delivery/open status via Brevo">Emails</span>
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="font-semibold">
                    <span
                      onClick={() => navigate(`/job/${job.id}`)}
                      data-testid={`view-job-${job.job_number}`}
                      title="Open job"
                      style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {job.job_number}
                    </span>
                  </TableCell>
                  <TableCell>{job.customer_name}</TableCell>
                  <TableCell>{job.mobile}</TableCell>
                  <TableCell>{job.status}</TableCell>
                  <TableCell>{job.job_type}</TableCell>
                  <TableCell>{formatDate(job.date_in)}</TableCell>
                  <TableCell>${parseFloat(job.total_amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{job.closed ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {/* View button removed Feb 2026 — the Job # column links
                          to the same page, so this button was redundant. */}
                      {settings?.twilio_enabled && job.mobile && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openSmsModal(job)}
                          data-testid={`sms-job-${job.job_number}`}
                          title="Send SMS"
                          style={{ width: '32px', height: '32px', padding: 0, color: '#475569' }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          </svg>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printSticker(job)}
                        data-testid={`sticker-job-${job.job_number}`}
                        title="Print Sticker"
                        style={{ width: '32px', height: '32px', padding: 0, color: '#475569' }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z"/>
                          <line x1="7" y1="7" x2="7.01" y2="7"/>
                        </svg>
                      </Button>
                      <EmailStatusIcons
                        summary={emailStatusByJob[job.id]}
                        jobId={job.id}
                        apiBase={API}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {jobs.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              No jobs found matching your filters
            </div>
          )}

          {/* Totals footer — aggregates across ALL filtered jobs, not just this page */}
          {totalJobs > 0 && (
            <div
              data-testid="all-jobs-totals"
              style={{
                marginTop: '1rem',
                padding: '1rem 1.25rem',
                background: 'linear-gradient(135deg, #f8fafc, #eef2ff)',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '2rem',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total jobs matching filters</div>
                <div data-testid="totals-count" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>{totalJobs.toLocaleString()}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total (ex. GST)</div>
                <div data-testid="totals-ex-gst" style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a' }}>${totals.total_ex_gst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>GST</div>
                <div data-testid="totals-gst" style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a' }}>${totals.total_gst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total (inc. GST)</div>
                <div data-testid="totals-inc-gst" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#059669' }}>${totals.total_inc_gst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Paid</div>
                <div data-testid="totals-paid" style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a' }}>${totals.total_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SMS Modal */}
      <Dialog open={smsModalOpen} onOpenChange={setSmsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Send SMS to {smsJob?.customer_name}</DialogTitle>
          </DialogHeader>
          
          <div style={{ display: 'grid', gap: '1rem', padding: '1rem 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#f9fafb', padding: '0.75rem', borderRadius: '6px' }}>
              <span style={{ color: '#6b7280' }}>To:</span>
              <span style={{ fontWeight: '500' }}>{smsJob?.mobile}</span>
            </div>
            
            <div>
              <Label>Quick Templates</Label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {smsTemplates.map((template, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => applySmsTemplate(template)}
                  >
                    {template.name}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="sms-message">Message</Label>
              <textarea
                id="sms-message"
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Type your message..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
                data-testid="sms-message-input"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {smsMessage.length}/160 characters {smsMessage.length > 160 && `(${Math.ceil(smsMessage.length / 160)} SMS)`}
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsModalOpen(false)}>Cancel</Button>
            <Button onClick={sendSms} disabled={sendingSms || !smsMessage.trim()} data-testid="send-sms-btn">
              {sendingSms ? 'Sending...' : 'Send SMS'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllJobsPage;
